import { ReparoApi } from '@api/index';

export type SavedReportRecipeKind = 'template' | 'freeform';

export interface FreeformSavedSpec {
    serviceName: string;
    methodName: string;
    params: Record<string, unknown>;
    capabilities?: Record<string, unknown>;
    title: string;
    secondary?: {
        serviceName: string;
        methodName: string;
        params: Record<string, unknown>;
        capabilities?: Record<string, unknown>;
    };
    join?: {
        leftKey: string;
        rightKey: string;
        type: 'inner' | 'left';
    };
    /** A report built by one query over these named datasets. */
    datasets?: Array<{
        name: string;
        serviceName: string;
        methodName: string;
        params: Record<string, unknown>;
        capabilities?: Record<string, unknown>;
    }>;
    sql?: string;
}

export interface SavedReport {
    id: string;
    merchantId: string;
    ownerUserId: string;
    name: string;
    description?: string | null;
    recipeKind: SavedReportRecipeKind;
    /** Populated when recipeKind='template'. */
    templateId?: string | null;
    /** Populated when recipeKind='template'. */
    templateVersion?: number | null;
    /** Template params; empty {} for freeform. */
    params: Record<string, unknown>;
    /** Populated when recipeKind='freeform'. */
    freeformSpec?: FreeformSavedSpec | null;
    /** Last successful run, or null if never run. */
    lastRunAt?: string | null;
    /** Present when a newer template version exists. Always undefined for freeform. */
    availableUpgradeVersion?: number;
    createdAt: string;
    updatedAt: string;
}

export type CreateSavedReportInput =
    | {
          name: string;
          description?: string | null;
          recipeKind?: 'template';
          templateId: string;
          templateVersion: number;
          params: Record<string, unknown>;
      }
    | {
          name: string;
          description?: string | null;
          recipeKind: 'freeform';
          freeformSpec: FreeformSavedSpec;
      };

export interface UpdateSavedReportInput {
    name?: string;
    description?: string | null;
    /** Sent with the full recipe below to replace the report's recipe in place. */
    recipeKind?: SavedReportRecipeKind;
    templateId?: string;
    templateVersion?: number;
    params?: Record<string, unknown>;
    freeformSpec?: FreeformSavedSpec;
}

export const createSavedReport = async (
    input: CreateSavedReportInput,
): Promise<SavedReport> => {
    return ReparoApi<SavedReport>({
        method: 'POST',
        url: '/saved-reports',
        body: JSON.stringify(input),
    });
};

export const listSavedReports = async (): Promise<SavedReport[]> => {
    return ReparoApi<SavedReport[]>({
        method: 'GET',
        url: '/saved-reports',
    });
};

export const updateSavedReport = async (
    id: string,
    input: UpdateSavedReportInput,
): Promise<SavedReport> => {
    return ReparoApi<SavedReport>({
        method: 'PATCH',
        url: `/saved-reports/${id}`,
        body: JSON.stringify(input),
    });
};

export const deleteSavedReport = async (id: string): Promise<void> => {
    return ReparoApi<void>({
        method: 'DELETE',
        url: `/saved-reports/${id}`,
    });
};

/**
 * Bumps the saved report's pinned templateVersion to the latest. Backend
 * re-validates params against the new schema and returns 422 (with a
 * structured `issues` array on err.error.data) if they don't fit.
 */
export const upgradeSavedReport = async (id: string): Promise<SavedReport> => {
    return ReparoApi<SavedReport>({
        method: 'POST',
        url: `/saved-reports/${id}/upgrade`,
    });
};
